<template>
  <div class="invoices-layout">
    <main-navigation />


    <div class="invoices-page">
      <div class="invoices-container">
        
        <!-- Compact Header -->
        <div class="invoices-header">
          <div class="header-title">
            <h2>
              <span class="title-icon"><i class="bi bi-receipt"></i></span>
              Invoices
            </h2>
            <span class="invoices-count-badge">{{ invoices.length }} total</span>
          </div>
          
          <button 
            @click="createInvoices" 
            :disabled="loading"
            class="btn-generate"
            :class="{ 'btn-loading': loading }"
          >
            <span class="btn-icon" v-if="!loading">+</span>
            <span class="btn-spinner" v-if="loading"></span>
            <span class="btn-text">{{ loading ? "Generating..." : "Generate" }}</span>
          </button>

                 <button 
            @click="reconcileBillings" 
            :disabled="loading"
            class="btn-generate"
            :class="{ 'btn-loading': loading }"
          >
            <span class="btn-icon" v-if="!loading">+</span>
            <span class="btn-spinner" v-if="loading"></span>
            <span class="btn-text">{{ loading ? "Reconcilling..." : "Reconcile Bills" }}</span>
          </button>
        </div>

        <!-- Search Bar -->
        <div class="search-section">
          <div class="search-wrapper">
            <span class="search-icon"><i class="bi bi-search"></i></span>
            <input
              v-model="search"
              type="text"
              placeholder="Search by client name, phone or invoice..."
              class="search-input"
            />
            <button v-if="search" @click="search = ''" class="clear-search"><i class="bi bi-x"></i></button>
          </div>
        </div>

        <!-- Messages -->
        <transition name="fade">
          <div v-if="successMessage" class="message-box success">
            <i class="bi bi-check-circle-fill"></i> {{ successMessage }}
            <button @click="successMessage = ''" class="message-close"><i class="bi bi-x"></i></button>
          </div>
        </transition>

        <transition name="fade">
          <div v-if="errorMessage" class="message-box error">
            <i class="bi bi-exclamation-circle-fill"></i> {{ errorMessage }}
            <button @click="errorMessage = ''" class="message-close"><i class="bi bi-x"></i></button>
          </div>
        </transition>

        <!-- Invoice List -->
        <div class="invoices-list-container">
          <div v-if="invoicesLoading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading invoices...</p>
          </div>

          <div v-else-if="filteredInvoices.length" class="invoices-list">
            <div 
              v-for="inv in paginatedInvoices" 
              :key="inv.id" 
              class="invoice-item"
              :ref="'invoiceRef' + inv.id"
            >
              <div class="invoice-item-main">
                <!-- Client Info -->
                <div class="client-info">
                  <div class="client-avatar">{{ getInitials(inv.Tenant?.tenant_name || inv.client_name) }}</div>
                  <div class="client-details">
                    <router-link 
                      :to="`/invoice/${inv.id}`" 
                      class="client-link"
                    >
                      {{ inv.Tenant?.tenant_name || inv.client_name }}
                    </router-link>
                    <span class="client-phone">{{ inv.Tenant?.tenant_phone || 'No phone' }}</span>
                  </div>
                </div>

                <!-- Invoice Details -->
                <div class="invoice-details">
                  <router-link 
                    :to="`/invoice/${inv.id}`" 
                    class="invoice-name"
                  >
                    {{ inv.invoice_name }}
                  </router-link>
                  <span class="invoice-description">{{ inv.description || 'No description' }}</span>
                </div>

                <!-- Period & Amount -->
                <div class="invoice-meta">
                  <span class="invoice-period">
                    {{ formatMonth(inv.invoice_month) }} {{ inv.invoice_year }}
                  </span>
                  <span class="invoice-amount" v-if="inv.amount">
                    KES {{ formatAmount(inv.amount) }}
                  </span>
                </div>

                <!-- Status & Actions -->
                <div class="invoice-actions ">
                  <span class="status-badge" :class="getStatusClass(inv)">
                    {{ inv.processed ? 'Processed' : 'Pending' }}
                  </span>
                    <button class="action-btn view-btn" @click="viewInvoiceDetails(inv)" title="View Details">
                      <i class="bi bi-eye"></i>
                    </button>
                 <!-- <div class="action-buttons">
                    <button class="action-btn view-btn" @click="viewInvoiceDetails(inv)" title="View Details">
                      <i class="bi bi-eye"></i>
                    </button>
                    <button class="action-btn download-btn" @click="downloadInvoice()" title="Download">
                      ⬇️
                    </button> 
                  </div>-->
                </div>
              </div>

              <!-- Unit Info (if available) -->
              <div v-if="inv.unit_id" class="invoice-footer">
                <span class="unit-badge">Unit #{{ inv.unit_id }}</span>
                <span class="created-date">Created: {{ formatDate(inv.createdAt) }}</span>
              </div>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="pagination">
              <button 
                @click="currentPage--" 
                :disabled="currentPage === 1"
                class="page-btn"
              >
                ←
              </button>
              <span class="page-info">
                {{ currentPage }} / {{ totalPages }}
              </span>
              <button 
                @click="currentPage++" 
                :disabled="currentPage === totalPages"
                class="page-btn"
              >
                →
              </button>
            </div>
          </div>

          <div v-else class="empty-state">
            <div class="empty-icon"><i class="bi bi-inbox"></i></div>
            <p v-if="search">No results for "{{ search }}"</p>
            <p v-else>No invoices yet</p>
            <button v-if="!invoices.length" class="refresh-btn" @click="getInvoices">
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export default {
  data() {
    return {
      loading: false,
      invoicesLoading: false,
      invoices: [],
      search: "",
      successMessage: "",
      errorMessage: "",
      currentPage: 1,
      itemsPerPage: 10
    };
  },

  async mounted() {
    await this.getInvoices();
  },

  computed: {
    filteredInvoices() {
      if (!this.search) return this.invoices;

      const term = this.search.toLowerCase().trim();

      return this.invoices.filter(inv => {
        const tenantName = inv.Tenant?.tenant_name?.toLowerCase() || '';
        const clientName = inv.client_name?.toLowerCase() || '';
        const phone = inv.Tenant?.tenant_phone?.toLowerCase() || '';
        const invoiceName = inv.invoice_name?.toLowerCase() || '';
        const description = inv.description?.toLowerCase() || '';
        
        return (
          tenantName.includes(term) ||
          clientName.includes(term) ||
          phone.includes(term) ||
          invoiceName.includes(term) ||
          description.includes(term)
        );
      });
    },

    paginatedInvoices() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredInvoices.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredInvoices.length / this.itemsPerPage);
    }
  },

  methods: {
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },

    formatMonth(month) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months[month - 1] || month;
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      return Number(amount).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-KE', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    },

    getStatusClass(inv) {
      if (inv.processed) return 'status-processed';
      return 'status-pending';
    },

    async getInvoices() {
      this.invoicesLoading = true;
      this.errorMessage = "";

      try {
        const response = await apiFetch("/get/invoices");
        
        // Handle different response structures
        if (Array.isArray(response)) {
          this.invoices = response;
        } else if (response?.data) {
          this.invoices = response.data;
        } else if (response?.invoices) {
          this.invoices = response.invoices;
        } else {
          this.invoices = [];
        }
        
        console.log("Invoices loaded:", this.invoices);
        this.currentPage = 1;
      } catch (error) {
        console.error("Fetch invoices error:", error);
        this.errorMessage = "Failed to load invoices";
        this.invoices = [];
      } finally {
        this.invoicesLoading = false;
      }
    },

    async createInvoices() {
      if (!confirm("Generate invoices for this month?")) return;

      this.loading = true;
      this.successMessage = "";
      this.errorMessage = "";

      try {
        const response = await apiFetch("/create/invoices", {
          method: "POST",
        });

        const count = response.created || response.total || 0;
        this.successMessage = `Generated ${count} invoices`;

        await this.getInvoices();

        setTimeout(() => {
          this.successMessage = "";
        }, 3000);

      } catch (error) {
        console.error(error);
        this.errorMessage = "Failed to generate invoices";
      } finally {
        this.loading = false;
      }
    },

  async reconcileBillings() {
  if (!confirm("Run billing reconciliation?")) return;

  this.loading = true;
  this.successMessage = "";
  this.errorMessage = "";

  try {
    const response = await apiFetch("/billing/reconciliation", {
      method: "POST",
    });

    // ✅ get updated count from backend
    const count = response.updated || 0;

    this.successMessage = `${count} bill(s) successfully reconciled`;

    // optional refresh
    await this.getInvoices();

    // auto-clear message
    setTimeout(() => {
      this.successMessage = "";
    }, 3000);

  } catch (error) {
    console.error(error);
    this.errorMessage = "Failed to reconcile billings";

    setTimeout(() => {
      this.errorMessage = "";
    }, 3000);
  } finally {
    this.loading = false;
  }
},
    viewInvoiceDetails(inv) {
      this.$router.push(`/invoice/${inv.id}`);
    },

    downloadInvoice(invoice) {
      const refKey = 'invoiceRef' + invoice.id;
      const element = this.$refs[refKey]?.[0] || this.$refs[refKey];

      html2canvas(element, { scale: 2 }).then(canvas => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");

        const imgWidth = 210;
        const pageHeight = 295;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`${invoice.invoice_name}.pdf`);
      });
    }
  },

  watch: {
    search() {
      this.currentPage = 1;
    }
  }
};
</script>

<style scoped>
.invoices-layout {
  min-height: 100vh;
  background: var(--dash-bg);
}

.invoices-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.invoices-container {
  background: white;
  border-radius: var(--dash-radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

/* Compact Header */
.invoices-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--dash-line);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-title h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--dash-ink);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-icon {
  font-size: 1.25rem;
}

.invoices-count-badge {
  background: var(--dash-bg-alt);
  padding: 4px 10px;
  border-radius: var(--dash-radius-lg);
  font-size: 0.8rem;
  color: var(--dash-muted);
  font-weight: 500;
}

/* Generate Button */
.btn-generate {
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: var(--dash-pill);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  min-width: 100px;
  justify-content: center;
}

.btn-generate:hover:not(:disabled) {
  background: var(--dash-primary-hover);
  transform: translateY(-1px);
  box-shadow: var(--dash-shadow);
}

.btn-generate:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-icon {
  font-size: 1.1rem;
  font-weight: 600;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Search Section */
.search-section {
  padding: 12px 20px;
  border-bottom: 1px solid var(--dash-line);
  background: var(--dash-surface-2);
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: var(--dash-faint);
  font-size: 0.9rem;
}

.search-input {
  width: 100%;
  padding: 10px 35px 10px 35px;
  border: 1px solid var(--dash-line);
  border-radius: var(--dash-radius);
  font-size: 0.9rem;
  transition: all 0.2s;
  background: white;
}

.search-input:focus {
  outline: none;
  border-color: var(--dash-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.clear-search {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: var(--dash-faint);
  cursor: pointer;
  padding: 4px 8px;
  font-size: 0.9rem;
  border-radius: var(--dash-pill);
}

.clear-search:hover {
  background: var(--dash-bg-alt);
  color: var(--dash-muted);
}

/* Messages */
.message-box {
  margin: 12px 20px;
  padding: 10px 16px;
  border-radius: var(--dash-radius-xs);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid;
}

.message-box.success {
  background: var(--dash-success-soft);
  border-color: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.message-box.error {
  background: var(--dash-danger-soft);
  border-color: var(--dash-danger-soft);
  color: var(--dash-danger-ink);
}

.message-close {
  margin-left: auto;
  background: none;
  border: none;
  color: currentColor;
  opacity: 0.6;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: var(--dash-pill);
}

.message-close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.05);
}

/* Invoices List */
.invoices-list-container {
  padding: 20px;
}

.invoices-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.invoice-item {
  background: white;
  border: 1px solid var(--dash-line);
  border-radius: var(--dash-radius-sm);
  transition: all 0.2s;
  overflow: hidden;
}

.invoice-item:hover {
  border-color: var(--dash-line-strong);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.invoice-item-main {
  padding: 16px;
  display: grid;
  grid-template-columns: 1.5fr 2fr 1fr auto;
  gap: 16px;
  align-items: center;
}

/* Client Info */
.client-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.client-avatar {
  width: 40px;
  height: 40px;
  background: var(--dash-primary);
  border-radius: var(--dash-radius-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.client-details {
  min-width: 0;
  flex: 1;
}

.client-link {
  color: var(--dash-ink);
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.client-link:hover {
  color: var(--dash-primary);
  text-decoration: underline;
}

.client-phone {
  font-size: 0.8rem;
  color: var(--dash-muted);
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Invoice Details */
.invoice-details {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.invoice-name {
  color: var(--dash-text);
  font-weight: 500;
  font-size: 0.95rem;
  text-decoration: none;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.invoice-name:hover {
  color: var(--dash-primary);
  text-decoration: underline;
}

.invoice-description {
  font-size: 0.8rem;
  color: var(--dash-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Invoice Meta */
.invoice-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.invoice-period {
  font-size: 0.9rem;
  color: var(--dash-text);
  font-weight: 500;
}

.invoice-amount {
  font-size: 0.85rem;
  color: var(--dash-success);
  font-weight: 600;
}

/* Invoice Actions */
.invoice-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-items:center;
  gap: 8px;
}

.status-badge {
  padding: 4px 10px;
  border-radius: var(--dash-radius-lg);
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;

}

.status-pending {
  background: var(--dash-warning-soft);
  color: var(--dash-warning-ink);
}

.status-processed {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

.action-buttons {
  display: flex;
  flex-direction: row;
  gap: 6px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--dash-pill);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-btn {
  background: var(--dash-primary-soft);
  color: var(--dash-primary-ink);
}

.view-btn:hover {
  background: var(--dash-primary-line);
}

.download-btn {
  background: var(--dash-primary-soft);
  color: var(--dash-primary-hover);
}

.download-btn:hover {
  background: var(--dash-primary-soft);
}

/* Invoice Footer */
.invoice-footer {
  padding: 10px 16px;
  background: var(--dash-surface-2);
  border-top: 1px solid var(--dash-line);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
}

.unit-badge {
  background: var(--dash-line);
  padding: 2px 8px;
  border-radius: var(--dash-radius-xs);
  color: var(--dash-muted);
}

.created-date {
  color: var(--dash-faint);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 16px 0;
}

.page-btn {
  width: 36px;
  height: 36px;
  border: 1px solid var(--dash-line);
  background: white;
  border-radius: var(--dash-radius-xs);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-btn:hover:not(:disabled) {
  background: var(--dash-bg);
  border-color: var(--dash-line-strong);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.9rem;
  color: var(--dash-muted);
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--dash-bg-alt);
  border-top-color: var(--dash-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-state p {
  color: var(--dash-muted);
  margin: 0;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-sm);
  border: 1px dashed var(--dash-line-strong);
}

.empty-icon {
  font-size: 2.25rem;
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-state p {
  color: var(--dash-muted);
  margin: 0 0 16px 0;
  font-size: 0.95rem;
}

.refresh-btn {
  padding: 8px 20px;
  border: none;
  border-radius: var(--dash-radius-xs);
  background: var(--dash-primary);
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-btn:hover {
  background: var(--dash-primary-hover);
}

/* Animations */
@keyframes spin {
  to { transform: rotate(360deg); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 1024px) {
  .invoice-item-main {
    grid-template-columns: 1.5fr 2fr 1fr;
    gap: 12px;
  }
  
  .invoice-actions {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
  }
}

@media (max-width: 768px) {
  .invoice-item-main {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .client-info {
    grid-row: 1;
  }
  
  .invoice-details {
    grid-row: 2;
  }
  
  .invoice-meta {
    grid-row: 3;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  
  .invoice-actions {
    display:flex;
    flex-direction: row;
    background-color:var(--dash-text);
    margin-top: 0;
  }
  
  .action-buttons {
    margin-left: auto;
  }
}

@media (max-width: 480px) {
  .invoices-page {
    padding: 10px;
  }
  
  .invoices-header {
    padding: 12px 16px;
  }
  
  .header-title h2 {
    font-size: 1.1rem;
  }
  
  .btn-generate {
    padding: 6px 12px;
    min-width: 80px;
    font-size: 0.85rem;
  }
  
  .search-section {
    padding: 8px 16px;
  }
  
  .invoices-list-container {
    padding: 16px;
  }
  
  .invoice-item-main {
    padding: 12px;
  }
  
  .client-avatar {
    width: 36px;
    height: 36px;
    font-size: 0.85rem;
  }
}
</style>